import { useState } from "react";

import type {
  MarketingBannerRow,
  MarketingBannersTranslations,
  RenameBannerHandlers,
} from "./types";

import { BottomSheet } from "../BottomSheet";
import { BannerBottomSheetMenu } from "./BannerBottomSheetMenu";
import { BannerBottomSheetRename } from "./BannerBottomSheetRename";
import { RenameBannerFooter } from "./RenameBannerFooter";

type BottomSheetView = "menu" | "renameBanner";

type BannerBottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  record: MarketingBannerRow;
  translations: MarketingBannersTranslations;
  onView: () => void;
  /** Fired before the menu's "Copy HTML code" item writes to the clipboard. */
  onCopy?: () => void;
  onDownload: () => void;
  /** Fired when the menu's "Rename" item opens the rename view. */
  onRenameClick?: () => void;
  /**
   * Fired when the rename view's Cancel button is pressed. Unlike Desktop's
   * `RenameBannerModal` (whose Cancel calls the shared rename hook's own
   * `cancelRename`), this sheet's Cancel button only resets local view state
   * — `RenameBannerHandlers` exposes no cancel method for it to call — so
   * tracking must be threaded here rather than inside the shared rename hook.
   */
  onRenameCancel?: () => void;
  renameBanner: RenameBannerHandlers;
  performanceHref?: string;
  onViewPerformance?: () => void;
  onNavigateToPerformance?: (href: string) => void;
};

export const BannerBottomSheet = ({
  isOpen,
  onClose,
  record,
  translations,
  onView,
  onCopy,
  onDownload,
  onRenameClick,
  onRenameCancel,
  renameBanner,
  performanceHref,
  onViewPerformance,
  onNavigateToPerformance,
}: BannerBottomSheetProps) => {
  const [view, setView] = useState<BottomSheetView>("menu");

  const handleClose = () => {
    setView("menu");
    onClose();
  };

  const renameFooter = (
    <RenameBannerFooter
      isSaving={renameBanner.isSaving}
      isSaveDisabled={renameBanner.isSaveDisabled}
      translations={translations}
      onCancel={() => {
        onRenameCancel?.();
        setView("menu");
      }}
      onSave={async () => {
        const success = await renameBanner.save();
        if (success) handleClose();
      }}
    />
  );

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      isDraggable
      contentContainerClassName="p-[unset]"
      footer={view === "renameBanner" ? renameFooter : undefined}
    >
      {view === "menu" && (
        <BannerBottomSheetMenu
          banner={record}
          translations={translations}
          onView={() => {
            handleClose();
            onView();
          }}
          onCopy={onCopy}
          onRename={() => {
            onRenameClick?.();
            renameBanner.startRename(record);
            setView("renameBanner");
          }}
          onDownload={() => {
            handleClose();
            onDownload();
          }}
          performanceHref={performanceHref}
          onViewPerformance={onViewPerformance}
          onNavigateToPerformance={onNavigateToPerformance}
        />
      )}
      {view === "renameBanner" && (
        <BannerBottomSheetRename
          name={renameBanner.name}
          error={renameBanner.error}
          translations={translations}
          onNameChange={renameBanner.changeName}
          onBack={() => setView("menu")}
          onClose={handleClose}
        />
      )}
    </BottomSheet>
  );
};
